import { IidTokenSession } from 'interfaces/session.interface';
import { Nullable } from 'interfaces/Nullable.ts';

/**
 * Cognito `exp` claims are epoch SECONDS, not milliseconds. Every helper here
 * works in seconds so the unit never has to be guessed at a call site.
 */

/** Refresh this many seconds before the real expiry, so a slow request cannot
 *  start on a token that dies mid-flight. */
export const TOKEN_EXPIRY_SKEW_SECONDS = 120;

export function getTokenExpiration(
  tokenSession: Nullable<Partial<IidTokenSession>>
): Nullable<number> {
  return tokenSession?.payload?.exp ?? null;
}

/**
 * Reads `exp` out of a raw JWT without verifying it. The signature is checked
 * server-side on every call; here we only need to know when to stop using the
 * token, so an unverified read of a public claim is enough.
 */
export function getJwtExpiration(jwt: Nullable<string>): Nullable<number> {
  if (!jwt) return null;
  const payload = jwt.split('.')[1];
  if (!payload) return null;
  try {
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const claims = JSON.parse(atob(normalized)) as { exp?: number };
    return typeof claims.exp === 'number' ? claims.exp : null;
  } catch (error) {
    console.warn('Could not read expiration from token', error);
    return null;
  }
}

/** A null expiry means "unknown", which is treated as not-expired: an unknown
 *  expiry must never be the reason a field user loses their session. */
export function isTokenExpired(expiresAt: Nullable<number>, skewSeconds = 0): boolean {
  if (!expiresAt) return false;
  return expiresAt - skewSeconds <= Math.floor(Date.now() / 1000);
}
